import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  TrendingUp,
  MapPin,
  DollarSign,
  Percent,
  Clock,
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  FileText,
  User,
  Calendar
} from 'lucide-react';
import API_CONSTRUCTOR from '../../models/api-model';
import {
  formatCurrency,
  formatPercentage,
  formatDate,
  formatDateTime,
  getStatusColor,
  getStatusLabel,
  getPropertyLocation,
  calculateROI
} from '../../utils/investorUtils';
import { toast } from 'sonner';

export const InvestmentDetail = () => {
  const { commitmentId } = useParams();
  const navigate = useNavigate();

  const [commitment, setCommitment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load commitment details
  const loadCommitment = async () => {
    try {
      setLoading(true);
      setError(null);

      const api = API_CONSTRUCTOR();
      const response = await api.get(`/commitments/${commitmentId}`);

      setCommitment(response.data);
    } catch (err) {
      console.error('Error loading commitment:', err);
      setError(err.response?.data?.error || 'Failed to load commitment details');
      toast.error('Failed to load commitment');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCommitment();
  }, [commitmentId]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error || !commitment) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate('/my-investments')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to My Investments
        </Button>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-4" />
            <p className="text-red-600 text-center">{error || 'Commitment not found'}</p>
            <div className="text-center mt-4">
              <Button onClick={loadCommitment}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const roi = calculateROI(commitment.commitment_amount, commitment.expected_total_return);
  const guaranteedReturnAmount = commitment.expected_total_return - commitment.commitment_amount - commitment.expected_profit_share;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/my-investments')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to My Investments
        </Button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Badge className={getStatusColor(commitment.commitment_status)}>
                {getStatusLabel(commitment.commitment_status)}
              </Badge>
              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                {formatPercentage(commitment.commitment_percentage, 0)} Investment
              </Badge>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Investment Details</h1>
            <p className="text-gray-600 flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {commitment.property_address || 'Property Address'}
            </p>
            {commitment.property_city && (
              <p className="text-sm text-muted-foreground ml-7">
                {getPropertyLocation(commitment)}
              </p>
            )}
          </div>

          <Button variant="outline" onClick={() => navigate(`/opportunities/${commitment.query_id}`)}>
            View Opportunity
          </Button>
        </div>
      </div>

      {/* Investment Summary */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Your Investment Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Investment Amount</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(commitment.commitment_amount)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Expected Return</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(commitment.expected_total_return)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Profit Share</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(commitment.expected_profit_share)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Expected ROI</p>
              <p className="text-2xl font-bold text-purple-600">{formatPercentage(roi, 1)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 pt-6 border-t">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Guaranteed Return</p>
              <p className="text-lg font-semibold">{formatCurrency(guaranteedReturnAmount)}</p>
              <p className="text-xs text-muted-foreground">Based on return rate</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Net Profit</p>
              <p className="text-lg font-semibold text-green-600">
                {formatCurrency(commitment.expected_total_return - commitment.commitment_amount)}
              </p>
              <p className="text-xs text-muted-foreground">Return - Investment</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Commitment Percentage</p>
              <p className="text-lg font-semibold">{formatPercentage(commitment.commitment_percentage, 1)}</p>
              <p className="text-xs text-muted-foreground">Of total investment</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="mt-1 rounded-full bg-blue-100 p-2">
                <Clock className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Commitment Created</p>
                <p className="text-sm text-muted-foreground">{formatDateTime(commitment.created_at)}</p>
              </div>
            </div>

            {commitment.approved_at && (
              <div className="flex items-start gap-3">
                <div className="mt-1 rounded-full bg-green-100 p-2">
                  <User className="h-4 w-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Approved by Admin</p>
                  <p className="text-sm text-muted-foreground">{formatDateTime(commitment.approved_at)}</p>
                  {commitment.admin_decision_notes && (
                    <p className="text-sm text-gray-700 mt-1 italic">"{commitment.admin_decision_notes}"</p>
                  )}
                </div>
              </div>
            )}

            {commitment.rejected_at && (
              <div className="flex items-start gap-3">
                <div className="mt-1 rounded-full bg-red-100 p-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Rejected by Admin</p>
                  <p className="text-sm text-muted-foreground">{formatDateTime(commitment.rejected_at)}</p>
                  {commitment.admin_decision_notes && (
                    <p className="text-sm text-gray-700 mt-1 italic">"{commitment.admin_decision_notes}"</p>
                  )}
                </div>
              </div>
            )}

            {commitment.withdrawn_at && (
              <div className="flex items-start gap-3">
                <div className="mt-1 rounded-full bg-gray-100 p-2">
                  <AlertCircle className="h-4 w-4 text-gray-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">Withdrawn</p>
                  <p className="text-sm text-muted-foreground">{formatDateTime(commitment.withdrawn_at)}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Your Notes */}
      {(commitment.investor_notes || commitment.investor_questions) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Your Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {commitment.investor_notes && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Notes</p>
                <p className="text-gray-700 whitespace-pre-wrap">{commitment.investor_notes}</p>
              </div>
            )}
            {commitment.investor_questions && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Questions</p>
                <p className="text-gray-700 whitespace-pre-wrap">{commitment.investor_questions}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Admin Response */}
      {commitment.admin_response && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Admin Response
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-wrap">{commitment.admin_response}</p>
          </CardContent>
        </Card>
      )}

      {/* Payment Status (if applicable) */}
      {commitment.payment_received && (
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Payment Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Payment Amount:</span>
              <span className="font-semibold">{formatCurrency(commitment.payment_amount)}</span>
            </div>
            {commitment.payment_method && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Payment Method:</span>
                <span className="font-semibold">{commitment.payment_method}</span>
              </div>
            )}
            {commitment.payment_reference && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Reference:</span>
                <span className="font-mono text-sm">{commitment.payment_reference}</span>
              </div>
            )}
            {commitment.payment_received_date && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Date Received:</span>
                <span className="font-semibold">{formatDateTime(commitment.payment_received_date)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default InvestmentDetail;
